import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { X, Zap, BarChart3, Bell, Crown, Lock } from 'lucide-react';

const UpgradeModal = ({ isOpen, onClose, feature = 'this feature' }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      navigate('/app/billing');
    }
  };

  const features = [
    { icon: Zap, text: 'Unlimited market scans' },
    { icon: BarChart3, text: 'Unlimited AI article analysis' },
    { icon: Bell, text: 'Real-time anomaly alerts' }
  ];

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div 
        className="w-full max-w-md rounded-2xl p-6 relative"
        style={{
          background: 'linear-gradient(135deg, #0E1022 0%, #12162B 100%)',
          border: '1px solid rgba(124, 92, 255, 0.2)',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5), 0 0 100px rgba(124, 92, 255, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="upgrade-modal"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg transition-colors"
          style={{ color: '#71717A' }}
          onMouseEnter={(e) => e.target.style.color = '#A1A1AA'}
          onMouseLeave={(e) => e.target.style.color = '#71717A'}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Lock icon */}
        <div className="text-center mb-6">
          <div 
            className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4"
            style={{
              background: 'linear-gradient(135deg, rgba(124, 92, 255, 0.2) 0%, rgba(91, 140, 255, 0.1) 100%)',
              border: '1px solid rgba(124, 92, 255, 0.2)'
            }}
          >
            <Lock className="w-8 h-8" style={{ color: '#7C5CFF' }} />
          </div>
          <h2 
            className="text-2xl font-bold mb-2"
            style={{ color: '#F4F4F5' }}
          >
            Upgrade to Pro
          </h2>
          <p style={{ color: '#71717A' }}>
            {feature} requires a Pro subscription
          </p>
        </div>

        {/* Features list */}
        <div 
          className="rounded-xl p-4 mb-6"
          style={{
            background: 'rgba(124, 92, 255, 0.05)',
            border: '1px solid rgba(124, 92, 255, 0.1)'
          }}
        >
          <div className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(124, 92, 255, 0.1)' }}
                >
                  <feature.icon className="w-4 h-4" style={{ color: '#7C5CFF' }} />
                </div>
                <span style={{ color: '#A1A1AA' }}>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Price and Trial */}
        <div className="text-center mb-6">
          {/* Trial badge */}
          <div 
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mb-3"
            style={{
              background: 'rgba(74, 222, 128, 0.15)',
              color: '#4ade80',
              border: '1px solid rgba(74, 222, 128, 0.3)'
            }}
          >
            48-hour free trial
          </div>
          <p className="text-3xl font-bold" style={{ color: '#F4F4F5' }}>
            $14.99<span className="text-sm font-normal" style={{ color: '#71717A' }}>/month</span>
          </p>
          <p className="text-sm mt-1" style={{ color: '#71717A' }}>
            Try free for 48 hours, cancel anytime
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleUpgrade}
            className="w-full py-3 rounded-xl text-white font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #7C5CFF 0%, #6B4FE0 100%)',
              boxShadow: '0 4px 20px rgba(124, 92, 255, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.boxShadow = '0 4px 25px rgba(124, 92, 255, 0.4)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.boxShadow = '0 4px 20px rgba(124, 92, 255, 0.3)';
              e.target.style.transform = 'translateY(0)';
            }}
            data-testid="upgrade-modal-btn"
          >
            <Crown className="w-4 h-4" />
            {isAuthenticated ? 'Start Free Trial' : 'Sign Up to Start Trial'}
          </button>
          
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-medium text-sm transition-colors"
            style={{
              border: '1px solid rgba(113, 113, 122, 0.2)',
              color: '#71717A'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = 'rgba(113, 113, 122, 0.3)';
              e.target.style.color = '#A1A1AA';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'rgba(113, 113, 122, 0.2)';
              e.target.style.color = '#71717A';
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );

  // Render to body via Portal to avoid z-index conflicts
  return createPortal(modalContent, document.body);
};

export default UpgradeModal;
